import React, { useState, useEffect, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import axios from 'axios';
import './PlayerDetailModal.css';
import { displayName, isBlockedName } from './LeaderboardParts';

const PlayerDetailModal = ({ playerName, cluster, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const dialogRef = useRef(null);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const response = await axios.get('/api/ark/player_rankings/detail', {
          params: { name: playerName }
        });
        setDetail(response.data);
      } catch (err) {
        console.error('Error fetching player detail:', err);
      }
      setLoading(false);
    };
    fetchDetail();
  }, [playerName]);

  useEffect(() => {
    const handleEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Lock page scroll behind the dialog and move focus into it; hand focus
  // back to whatever opened it (the clicked row) on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
    };
  }, []);

  const formatKD = (kills, deaths) => {
    if (deaths === 0 && kills > 0) return <span className="kd-perfect">{kills}.0</span>;
    if (deaths === 0) return '0.0';
    return (kills / deaths).toFixed(1);
  };

  const cleanTribe = (name) => {
    if (!name) return null;
    const fixed = displayName(name);
    return fixed.startsWith('Tribe of ') ? fixed.slice(9) : fixed;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-content clip-corner"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="player-modal-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="modal-topline" aria-hidden="true" />
        <button type="button" className="modal-close clip-corner-sm" onClick={onClose} aria-label="Close">
          <FaTimes aria-hidden="true" />
        </button>

        {loading ? (
          <div className="modal-loading">
            <div className="skeleton-cell" style={{ width: '30%', height: 10, marginBottom: 18 }} />
            <div className="skeleton-cell" style={{ width: '65%', height: 40, marginBottom: 14 }} />
            <div className="skeleton-cell" style={{ width: '40%', height: 12, marginBottom: 28 }} />
            <div className="skeleton-cell" style={{ width: '100%', height: 120 }} />
          </div>
        ) : !detail || detail.clusters.length === 0 ? (
          <div className="modal-error">
            <p className="hud-label">{'// Player profile'}</p>
            <p id="player-modal-title" className="modal-error-text">Player not found on any cluster</p>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <p className="hud-label kicker modal-kicker"><span className="kicker-line" />Player profile</p>
              <h3 id="player-modal-title" className="modal-player-name font-display">{displayName(detail.playerName)}</h3>
              <div className="modal-subtitle hud-label">
                Found on {detail.clusters.length} cluster{detail.clusters.length > 1 ? 's' : ''}
              </div>
            </div>

            <div className="modal-clusters">
              {detail.clusters.map((c) => (
                <div key={c.cluster} className={`modal-cluster-card clip-corner-sm${c.cluster === cluster ? ' current-cluster' : ''}`}>
                  <div className="cluster-card-header">
                    <span className="cluster-card-name font-display">{c.clusterDisplay}</span>
                    {c.cluster === cluster && <span className="cluster-card-badge">Current</span>}
                    <span className="cluster-card-rank">#{c.killsRank} kills</span>
                  </div>
                  <div className="cluster-card-tribe">
                    {cleanTribe(c.tribeName) || <span className="tribe-solo">Solo</span>}
                  </div>
                  <div className="cluster-card-stats">
                    <div className="cluster-stat">
                      <span className="cluster-stat-val">{c.kills.toLocaleString()}</span>
                      <span className="cluster-stat-label">Kills</span>
                    </div>
                    <div className="cluster-stat">
                      <span className="cluster-stat-val">{c.deaths.toLocaleString()}</span>
                      <span className="cluster-stat-label">Deaths</span>
                    </div>
                    <div className="cluster-stat">
                      <span className="cluster-stat-val">{formatKD(c.kills, c.deaths)}</span>
                      <span className="cluster-stat-label">K/D</span>
                    </div>
                    <div className="cluster-stat">
                      <span className="cluster-stat-val">{c.dinoKills.toLocaleString()}</span>
                      <span className="cluster-stat-label">Dinos</span>
                    </div>
                    <div className="cluster-stat">
                      <span className="cluster-stat-val">{c.playTime ? (c.playTime / 60).toFixed(1) : '0'}h</span>
                      <span className="cluster-stat-label">Time</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!isBlockedName(detail.playerName) && (
              <a
                className="modal-profile-link hud-label"
                href={`https://mesark.net/players/${encodeURIComponent(detail.playerName)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Shareable profile on mesark.net &rarr;
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlayerDetailModal;
